import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import { colors } from '../ui-elements/Main/MainTheme';
import { PrefixOption, PrefixSearchInput } from '../inbox/PrefixSearchInput';
import { AccountInfoContext } from '../account/account-info';
import { SearchResult, SearchResultCard } from '../search/SearchResultCard';
import { avo } from '@/api/app/avo-client';

const PREFIXES: PrefixOption[] = [
  { prefix: 'tag', dataType: 'text', description: 'Search by tag applied to object', aliases: ['tag.name', 'tags', 'tags.name'] },
];

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const { subdomain } = useContext(AccountInfoContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestQuery = useRef<string>(null);

  const handleReset = () => {
    setSearchQuery('');
    setResults(null);
  };

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      handleReset();
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleSearch = useCallback(async (query: string) => {
    if (!query || query.trim().length < 2) {
      setResults(null);
      setIsLoading(false);
      return;
    }

    if (requestQuery.current === query) return;

    setIsLoading(true);
    requestQuery.current = query;
    try {
      const response = await avo.ajax<SearchResult[]>({
        route: '/v1/search',
        params: {
          subdomain,
          query: query.trim(),
          limit: '20',
        },
      });

      setResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
      requestQuery.current = null;
    }
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleResultClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <PrefixSearchInput
      subdomain={subdomain}
      value={searchQuery}
      onChange={handleSearchChange}
      onSubmit={handleSearch}
      onClear={handleReset}
      onClose={onClose}
      placeholder="Search across your account..."
      type="B"
      prefixes={PREFIXES}
    >
      <ResultsContainer>
        {isLoading && (
          <LoadingMessage>Searching...</LoadingMessage>
        )}

        {!isLoading && results && (
          <ResultsSection>
            <ResultsHeader>
              <ResultsCount>{results.length} result{results.length !== 1 ? 's' : ''}</ResultsCount>
            </ResultsHeader>
            {results.length < 1 ? (
              <NoResults>
                <NoResultsTitle>No results found</NoResultsTitle>
                <NoResultsText>Try adjusting your search query</NoResultsText>
              </NoResults>
            ) : (
              <ResultsList>
                {results.map((result) => (
                  <SearchResultCard
                    subdomain={subdomain}
                    key={`${result.type}-${result.id}`}
                    result={result}
                    onNavigate={handleResultClick}
                  />
                ))}
              </ResultsList>
            )}
          </ResultsSection>
        )}

        {results === null && searchQuery.length < 2 && (
          <HelpSection>
            <HelpTitle>Search Tips</HelpTitle>
            <HelpList>
              <HelpItem>
                <HelpItemTitle>Basic Search:</HelpItemTitle> Type any text to search across contacts and messages
              </HelpItem>
              <HelpItem>
                <HelpItemTitle>Advanced Search:</HelpItemTitle> Use prefixes like <Code>tag:</Code>
              </HelpItem>
              <HelpItem>
                <HelpItemTitle>Keyboard:</HelpItemTitle> Press <Kbd>Esc</Kbd> to close
              </HelpItem>
            </HelpList>
          </HelpSection>
        )}
      </ResultsContainer>
    </PrefixSearchInput>
  );
};

const ResultsContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 14px;
  color: ${colors.gray};
  font-family: Montserrat, sans-serif;
`;

const ResultsSection = styled.div``;

const ResultsHeader = styled.div`
  margin-bottom: 12px;
  border-bottom: 2px solid ${colors.white};
`;

const ResultsCount = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.darkGreen};
  font-family: Montserrat, sans-serif;

  background: ${colors.white};
  width: fit-content;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  padding: 3px 7px 2px 7px;
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const NoResults = styled.div`
  background: ${colors.white};
  text-align: center;
  padding: 20px;
  border-radius: 8px;
`;

const NoResultsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.darkGreen};
  margin: 0 0 8px 0;
  font-family: Montserrat, sans-serif;
`;

const NoResultsText = styled.p`
  font-size: 13px;
  color: ${colors.gray};
  margin: 0;
  font-family: Montserrat, sans-serif;
`;

const HelpSection = styled.div`
  background: ${colors.sand};
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${colors.beige};
`;

const HelpTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.darkGreen};
  margin: 0 0 12px 0;
  font-family: Montserrat, sans-serif;
`;

const HelpList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const HelpItem = styled.li`
  font-size: 13px;
  color: ${colors.gray};
  margin-bottom: 10px;
  font-family: Montserrat, sans-serif;
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
  }
`;

const HelpItemTitle = styled.span`
  font-weight: 600;
  color: ${colors.darkGreen};
`;

const Code = styled.code`
  background: ${colors.white};
  padding: 1px 4px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #19BE51;
  border: 1px solid ${colors.beige};
`;

const Kbd = styled.kbd`
  background: ${colors.white};
  border: 1px solid ${colors.beige};
  border-radius: 4px;
  padding: 2px 6px;
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.darkGreen};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

export default SearchModal;
